import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles, Users, Trophy, Crown, Ticket, Info } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import { offerSchema } from "../lib/schema";
import { useReglages } from "../lib/useReglages";
import { PRICE_BY_DURATION_CHILDREN, PRICE_BY_DURATION_ADULTS, CARTE_10, PARAMETRES, ACCUMULATION, PASSES, texteValiditeCarte10, texteSemestresCarte10 } from "../lib/pricing";
import { tarifsPage } from "../lib/reglesPrives";

// Les prix par durée viennent de la console (Réglages → Tarifs), servis par
// `GET /api/reglages` : un prix changé là s'affiche ici sans redéploiement. Le barème de
// repli de `pricing.js` prend le relais si la console ne répond pas. Idem pour les pass
// illimités, la carte 10 cours et les montants de règlement (taxe, réductions famille et
// accumulation, costumes, parrainage) : aucun montant n'est écrit dans cette page, ce sont
// ceux du formulaire d'inscription et de la facture.
const LIBELLE_DUREE = {
  90: "1h30 / semaine", 75: "1h15 / semaine", 60: "1h / semaine",
  45: "45 min / semaine", 30: "30 min / semaine",
};

const depuisGrille = (grille, durees) => durees
  .map((d) => ({ dur: LIBELLE_DUREE[d], price: grille[d] }))
  .filter((p) => p.price);

// « 25 » → « 25.– », « 22.5 » → « 22.50 » (un montant réglé au centime reste lisible).
const chf = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? `${v}.–` : v.toFixed(2);
};

// Les cours privés : durées et prix des Réglages › Cours privés (02.11.2026), servis par
// `GET /api/reglages` ; repli : « Chant / Théâtre · 1h — 90 » … « Danse · 1h — 120 ».

// Pass illimités : nom, prix et contenu viennent de `pricing.js` (prix des Réglages de la
// console, repli sinon) — le même objet que le formulaire d'inscription. Ici, seulement
// l'habillage de chaque carte.
const STYLE_PASS = {
  "infinity-enfant": { icon: Crown, color: "#FFC857" },
  "golden-enfant": { icon: Trophy, color: "#A78BFA" },
  "infinity-adulte": { icon: Sparkles, color: "var(--primary)" },
};
const STYLE_PASS_DEFAUT = { icon: Sparkles, color: "var(--primary)" };

const COMP_GROUPS = [
  { name: "Compétition", price: 990, sub: "Sur audition",
    includes: ["2 entraînements / semaine", "1 samedi / trimestre", "Inscription aux compétitions", "Costumes inclus"] },
  { name: "Compagnie Juniors", price: 990, sub: "Sur audition",
    includes: ["2 répétitions / semaine", "Réservation des salles spectacles", "1 samedi / semestre", "3 cours de chant privé/année OBLIGATOIRES (non inclus dans le prix, −20 % sur ces cours)"] },
];

export default function Tarifs() {
  const reglages = useReglages();
  const PRICE_CHILDREN = React.useMemo(
    () => depuisGrille(PRICE_BY_DURATION_CHILDREN, [90, 75, 60, 45, 30]),
    [reglages]);
  const PRICE_ADULTS = React.useMemo(
    () => [...depuisGrille(PRICE_BY_DURATION_ADULTS, [90, 75, 60]),
      { dur: `Carte ${CARTE_10.nb_seances} cours`, price: CARTE_10.prix }],
    [reglages]);
  const PRIVATE_LESSONS = React.useMemo(() => tarifsPage(), [reglages]);
  const passes = React.useMemo(
    () => PASSES.map((p) => ({ name: p.label, price: p.price, includes: p.includes || [],
      ...(STYLE_PASS[p.id] || STYLE_PASS_DEFAUT) })),
    [reglages]);
  const offersJsonLd = React.useMemo(() => [
    ...PRICE_CHILDREN.map((p) => offerSchema({ name: `Cours enfants — ${p.dur}`, price: p.price, category: "Semestre enfants", description: `Un cours de ${p.dur} pendant tout le semestre.` })),
    ...PRICE_ADULTS.map((p) => offerSchema({ name: `Cours adultes — ${p.dur}`, price: p.price, category: "Semestre adultes", description: `Cours adultes — ${p.dur}.` })),
    ...PRIVATE_LESSONS.map((p) => offerSchema({ name: `Cours privé — ${p.type}`, price: p.price, category: "Cours privé", description: `Cours individuel — ${p.type}.` })),
    ...passes.map((p) => offerSchema({ name: p.name, price: p.price, category: "Pass illimité", description: p.includes.join(" · ") })),
    ...COMP_GROUPS.map((p) => offerSchema({ name: p.name, price: p.price, category: "Compétition & Compagnie", description: `${p.sub} · ${p.includes.join(" · ")}` })),
  ], [PRICE_CHILDREN, PRICE_ADULTS, PRIVATE_LESSONS, passes]);
  const prixParSeance = CARTE_10.nb_seances ? CARTE_10.prix / CARTE_10.nb_seances : 0;

  useSEO({
    title: "Tarifs des cours de danse — 1000 ARTS Cugy VD",
    description: "Tarifs 2026-2027 : enfants dès CHF 200/semestre, adultes dès CHF 450/semestre. Pass illimités, rabais famille et rabais accumulation. École de danse à Cugy VD, à 10 min de Lausanne.",
    canonical: "/tarifs",
    jsonLd: offersJsonLd,
  });
  return (
    <div data-testid="page-tarifs" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
      <div className="eyebrow mb-3">Saison 2026 / 2027</div>
      <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-tight">
        Tarifs<br />& <span style={{ color: "var(--primary)" }}>formules</span>
      </h1>
      <p className="mt-6 text-lg text-[var(--muted)] max-w-2xl">
        Prix par semestre. Vacances scolaires et jours fériés déjà déduits.
      </p>

      {/* Children + Adults */}
      <section className="grid md:grid-cols-2 gap-6 mt-14" data-testid="tarifs-semester">
        <div className="card-flat" style={{ background: "var(--primary-tint)", borderColor: "transparent" }}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--primary-tint)" }}>
              <Users size={18} style={{ color: "var(--primary)" }} />
            </div>
            <div className="eyebrow">Enfants & Ados</div>
          </div>
          <h2 className="font-display text-3xl mb-6">Au semestre</h2>
          <ul className="space-y-3">
            {PRICE_CHILDREN.map((p) => (
              <li key={p.dur} className="flex items-baseline justify-between py-3 border-b border-[var(--border-soft)] last:border-0">
                <span className="text-[var(--text)]">{p.dur}</span>
                <span className="font-display text-xl" aria-hidden="true"><span style={{ color: "var(--primary)" }}>CHF</span> {p.price}.–</span>
                {/* Même information en une phrase complète : les extracteurs de texte
                    (robots d'indexation, chatbots, lecteurs d'écran) perdent les montants
                    quand ils sont découpés en petits éléments de mise en page. */}
                <span className="sr-only">Enfants et ados, {p.dur} : CHF {p.price}.– par semestre.</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="card-flat">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--primary-tint)" }}>
              <Sparkles size={18} style={{ color: "var(--primary)" }} />
            </div>
            <div className="eyebrow">Adultes</div>
          </div>
          <h2 className="font-display text-3xl mb-6">Au semestre</h2>
          <ul className="space-y-3">
            {PRICE_ADULTS.map((p) => (
              <li key={p.dur} className="flex items-baseline justify-between py-3 border-b border-[var(--border-soft)] last:border-0">
                <span className="text-[var(--text)]">{p.dur}</span>
                <span className="font-display text-xl" aria-hidden="true"><span style={{ color: "var(--primary)" }}>CHF</span> {p.price}.–</span>
                <span className="sr-only">Adultes, {p.dur} : CHF {p.price}.– par semestre.</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Encart dédié Carte 10 cours */}
      <section className="mt-6" data-testid="tarifs-carte10">
        <div className="rounded-3xl overflow-hidden" style={{ border: "1px solid var(--border-soft)", background: "#fff" }}>
          <div className="grid md:grid-cols-[280px_1fr] gap-0">
            {/* Colonne gauche : titre + prix */}
            <div className="p-8 flex flex-col justify-center" style={{ background: "linear-gradient(135deg, var(--primary), #66EAF0)", color: "#fff" }}>
              <div className="flex items-center gap-2 mb-3">
                <Ticket size={20} />
                <div className="eyebrow" style={{ color: "rgba(255,255,255,0.9)" }}>Formule flexible</div>
              </div>
              <h3 className="font-display text-3xl leading-tight">Carte<br />{CARTE_10.nb_seances} cours</h3>
              <div className="mt-5 font-display text-4xl" aria-hidden="true">
                <span style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.55em", fontWeight: 500 }}>CHF</span> {CARTE_10.prix}.<span style={{ fontSize: "0.55em" }}>–</span>
              </div>
              <span className="sr-only">Carte {CARTE_10.nb_seances} cours : CHF {chf(CARTE_10.prix)} pour {CARTE_10.nb_seances} cours, soit CHF {chf(prixParSeance)} par cours.</span>
              <div className="mt-1 text-sm" style={{ color: "rgba(255,255,255,0.85)" }}>soit CHF {chf(prixParSeance)}/cours</div>
            </div>

            {/* Colonne droite : conditions */}
            <div className="p-8">
              <p className="text-sm text-[var(--muted)] leading-relaxed mb-5">
                Une formule <strong>sans engagement semestriel</strong>, idéale pour bouger à ton rythme quand ton emploi du temps le permet. La carte donne droit à {CARTE_10.nb_seances} séances, <strong>{texteValiditeCarte10()}</strong> : elle se termine avec le semestre pendant lequel elle a été achetée, et non six mois plus tard — une carte prise en fin de semestre laisse donc moins de temps pour utiliser les séances.
              </p>

              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <Check size={16} className="mt-1 shrink-0" style={{ color: "var(--primary)" }} />
                  <div>
                    <div className="font-semibold text-[var(--text)]">Cours éligibles</div>
                    <div className="text-[var(--muted)]">{CARTE_10.disciplines.join(" · ")} · Hip-hop freestyle</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Check size={16} className="mt-1 shrink-0" style={{ color: "var(--primary)" }} />
                  <div>
                    <div className="font-semibold text-[var(--text)]">Validité</div>
                    <div className="text-[var(--muted)]">
                      Le semestre en cours uniquement — les séances non utilisées sont perdues
                      {texteSemestresCarte10().map((t) => (
                        <span key={t} className="block text-xs mt-1" data-testid="carte10-semestre">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Check size={16} className="mt-1 shrink-0" style={{ color: "var(--primary)" }} />
                  <div>
                    <div className="font-semibold text-[var(--text)]">Paiement</div>
                    <div className="text-[var(--muted)]">À l'école, en espèces ou par TWINT — pas de facture</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Check size={16} className="mt-1 shrink-0" style={{ color: "var(--primary)" }} />
                  <div>
                    <div className="font-semibold text-[var(--text)]">Une carte par cours</div>
                    <div className="text-[var(--muted)]">Nominative, rattachée au cours choisi : deux cours, deux cartes</div>
                  </div>
                </div>

                {/* Décision de l'école (08.10.2026) : un nouvel élève qui ne prend que la carte
                    règle sa taxe d'inscription avec elle, à l'école — sans facture. */}
                <div className="flex items-start gap-3 sm:col-span-2" data-testid="tarifs-carte10-taxe">
                  <Check size={16} className="mt-1 shrink-0" style={{ color: "var(--primary)" }} />
                  <div>
                    <div className="font-semibold text-[var(--text)]">Nouvel élève</div>
                    <div className="text-[var(--muted)]">Taxe d'inscription unique de CHF {chf(PARAMETRES.taxe_admin_premiere_inscription)}, réglée avec la carte (CHF {chf(CARTE_10.prix + PARAMETRES.taxe_admin_premiere_inscription)} pour une première carte). Si tu suis aussi un cours au semestre, elle figure sur ta facture semestrielle.</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-start gap-3 p-4 rounded-2xl" style={{ background: "#FFF8E7", border: "1px solid #F5D57A" }}>
                <Info size={16} className="mt-0.5 shrink-0" style={{ color: "#B7791F" }} />
                <div className="text-xs leading-relaxed" style={{ color: "#8B6914" }}>
                  <strong>À noter :</strong> la carte 10 cours est cumulable avec l'inscription à d'autres cours, mais elle <strong>n'ouvre pas droit à la réduction de CHF&nbsp;{chf(ACCUMULATION.cours_2)}</strong> sur le 2<sup>e</sup> cours.
                </div>
              </div>

              {/* La carte a son propre formulaire depuis le 08.10.2026 (décision de l'école). */}
              <Link to="/inscription/carte-10" data-testid="tarifs-carte10-cta" className="btn-primary mt-6">
                <Ticket size={16} /> S'inscrire avec la carte 10 cours <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Discounts */}
      <section className="mt-10 grid md:grid-cols-2 gap-6" data-testid="tarifs-discounts">
        <div className="card-flat" style={{ background: "var(--primary)", color: "#fff", borderColor: "transparent" }}>
          <div className="eyebrow mb-2" style={{ color: "rgba(255,255,255,0.85)" }}>Accumulation de cours</div>
          <h3 className="font-display text-2xl mb-4">Plus tu danses, moins tu paies</h3>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-3"><Check size={16} /> <span><strong>2 cours</strong> · CHF {chf(ACCUMULATION.cours_2)} sur le 2<sup>e</sup></span></li>
            <li className="flex items-center gap-3"><Check size={16} /> <span><strong>3 cours</strong> · CHF {chf(ACCUMULATION.cours_3)} sur le 3<sup>e</sup></span></li>
            <li className="flex items-center gap-3"><Check size={16} /> <span><strong>4 cours et +</strong> · CHF {chf(ACCUMULATION.cours_suivant)} sur les supplémentaires</span></li>
          </ul>
          <p className="text-xs mt-4 italic" style={{ color: "rgba(255,255,255,0.75)" }}>Non valable sur les cours de compétition et de compagnie.</p>
        </div>

        <div className="card-flat" style={{ background: "var(--primary-tint)", borderColor: "transparent" }}>
          <div className="eyebrow mb-2">Tarifs famille</div>
          <h3 className="font-display text-2xl mb-4">La danse, ça se partage</h3>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-3"><Check size={16} style={{ color: "var(--primary)" }} /> <span><strong>2<sup>e</sup> enfant</strong> · CHF {chf(PARAMETRES.reduction_2eme_enfant)} de réduction</span></li>
            <li className="flex items-center gap-3"><Check size={16} style={{ color: "var(--primary)" }} /> <span><strong>3<sup>e</sup> enfant</strong> · CHF {chf(PARAMETRES.reduction_3eme_enfant)} de réduction</span></li>
            <li className="flex items-center gap-3"><Check size={16} style={{ color: "var(--primary)" }} /> <span><strong>4<sup>e</sup> enfant et +</strong> · CHF {chf(PARAMETRES.reduction_4eme_enfant)} de réduction</span></li>
          </ul>
          <p className="text-xs text-[var(--muted)] mt-4 italic">Réservé aux enfants d'une même famille vivant sous le même toit.</p>
        </div>
      </section>

      {/* PASSES */}
      <section className="mt-20" data-testid="tarifs-passes">
        <div className="eyebrow mb-3">Pass illimités</div>
        <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-10">
          Danse <span style={{ color: "var(--primary)" }}>sans limites</span>
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {passes.map((p) => (
            <div key={p.name} className="card-flat flex flex-col" style={{ borderTop: `4px solid ${p.color}` }}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: p.color === "var(--primary)" ? "var(--primary-tint)" : `${p.color}22` }}>
                <p.icon size={22} style={{ color: p.color }} />
              </div>
              <div className="font-display text-2xl">{p.name}</div>
              <div className="font-display text-4xl mt-3" aria-hidden="true">
                <span style={{ color: p.color }}>CHF</span> {p.price}.–
              </div>
              <span className="sr-only">{p.name} : CHF {p.price}.– par semestre.</span>
              <ul className="mt-5 space-y-2 text-sm flex-1">
                {p.includes.map((inc) => (
                  <li key={inc} className="flex items-start gap-2"><Check size={14} style={{ color: p.color, marginTop: 4, flexShrink: 0 }} /> <span>{inc}</span></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* COMPETITION */}
      <section className="mt-20 grid md:grid-cols-2 gap-6" data-testid="tarifs-compet">
        {COMP_GROUPS.map((g) => (
          <div key={g.name} className="card-flat">
            <div className="w-10 h-10 rounded-full flex items-center justify-center mb-4" style={{ background: "var(--primary-tint)" }}>
              <Trophy size={18} style={{ color: "var(--primary)" }} />
            </div>
            <div className="font-display text-2xl">{g.name}</div>
            <div className="text-xs text-[var(--muted)] mt-1 uppercase tracking-[0.15em]">{g.sub}</div>
            <div className="font-display text-4xl mt-4" aria-hidden="true">
              <span style={{ color: "var(--primary)" }}>CHF</span> {g.price}.–
            </div>
            <span className="sr-only">{g.name} : CHF {g.price}.– par semestre.</span>
            <ul className="mt-5 space-y-2 text-sm">
              {g.includes.map((i) => <li key={i} className="flex items-start gap-2"><Check size={14} style={{ color: "var(--primary)", marginTop: 4, flexShrink: 0 }} /> <span>{i}</span></li>)}
            </ul>
          </div>
        ))}
      </section>

      {/* Private */}
      <section className="mt-20" data-testid="tarifs-private">
        <div className="card-flat" style={{ background: "var(--primary)", borderColor: "transparent", color: "#fff", padding: "40px 32px" }}>
          <div className="eyebrow mb-3" style={{ color: "rgba(255,255,255,0.85)" }}>Cours privés</div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-8" style={{ color: "#fff" }}>Sur mesure</h2>
          <div className="rounded-xl overflow-hidden" style={{ background: "rgba(255,255,255,0.1)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.18)" }}>
            {PRIVATE_LESSONS.map((p, i) => (
              <div key={p.type} className="grid grid-cols-2 px-6 py-4 items-center" style={{ borderBottom: i < PRIVATE_LESSONS.length - 1 ? "1px solid rgba(255,255,255,0.15)" : "none" }}>
                <span className="text-white">{p.type}</span>
                <span className="font-display text-xl text-right text-white" aria-hidden="true"><span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85em" }}>CHF</span> {p.price}.–</span>
                <span className="sr-only">Cours privé, {p.type} : CHF {p.price}.–</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINE PRINT */}
      <section className="mt-20 grid md:grid-cols-2 gap-6" data-testid="tarifs-rules">
        <div className="card-flat">
          <div className="eyebrow mb-3">Conditions</div>
          <ul className="space-y-2 text-sm text-[var(--text)]">
            <li>· Engagement minimum d'un semestre</li>
            <li>· <strong>Inscription en cours de semestre :</strong> le tarif est réduit au prorata des mois restants. Le montant exact vous est confirmé par l'école avec la facture.</li>
            <li>· Factures distribuées en début de semestre · 30 jours pour payer</li>
            <li>· Taxe administrative unique <strong>CHF {chf(PARAMETRES.taxe_admin_premiere_inscription)}</strong> par élève, à sa première inscription à l'école (réglée avec la carte pour un élève qui ne prend que la carte {CARTE_10.nb_seances} cours)</li>
            <li>· Résiliation par écrit au moins 2 mois avant le semestre suivant</li>
            <li>· Paiement en 3 fois possible sur accord de la direction</li>
          </ul>
        </div>
        <div className="card-flat" style={{ background: "var(--primary-tint)", borderColor: "transparent" }}>
          <div className="eyebrow mb-3">Spectacle</div>
          <ul className="space-y-2 text-sm text-[var(--text)]">
            <li>· Participation <strong>obligatoire pour les enfants</strong>, facultative pour les adultes</li>
            <li>· Adultes : nous prévenir par e-mail avant le 31 décembre (31 janvier au plus tard)</li>
            <li>· Répétition générale obligatoire pour qui participe</li>
            <li>· <strong>Costume de spectacle, facturé avec le 2<sup>e</sup> semestre</strong></li>
            <li>· <strong>CHF {chf(PARAMETRES.costume_enfant)}</strong> par enfant et par cours — pour deux spectacles</li>
            <li>· <strong>CHF {chf(PARAMETRES.costume_adulte)}</strong> par adulte et par cours — pour un spectacle</li>
            <li>· Compétition et compagnie : costume compris dans le forfait</li>
          </ul>
        </div>
      </section>

      {/* PARRAINAGE */}
      <section className="mt-16 rounded-3xl p-8 md:p-12" style={{ background: "var(--primary-tint)", border: "2px dashed var(--primary)" }} data-testid="tarifs-parrainage">
        <div className="eyebrow mb-2" style={{ color: "var(--primary)" }}>🎁 Parrainage</div>
        <h3 className="font-display text-2xl md:text-3xl tracking-tight">
          Gagne <span style={{ color: "var(--primary)" }}>CHF {chf(PARAMETRES.parrainage)}</span> en invitant un·e ami·e
        </h3>
        <p className="mt-3 text-[var(--text)] max-w-3xl">
          Grâce au parrainage, obtiens <strong>CHF {chf(PARAMETRES.parrainage)} de réduction</strong> sur ta prochaine facture. Un élève invite un·e ami·e à rejoindre 1000 Arts (tous les cours). L'ami·e n'a qu'à indiquer ton nom dans le formulaire lors de son <strong>inscription au cours semestriel</strong>.
        </p>
        <p className="mt-3 text-sm text-[var(--muted)] italic max-w-3xl">
          <strong>Important&nbsp;:</strong> le parrainage n'est validé que si la personne parrainée s'inscrit officiellement à un cours à l'année. Un simple cours d'essai ou une participation ponctuelle ne suffit pas à activer la réduction.
        </p>
      </section>

      {/* SWICA — Remboursement assurance */}
      <section className="mt-16 rounded-3xl p-8 md:p-12 border-2 border-[var(--primary)]" style={{ background: "var(--primary-tint)" }} data-testid="tarifs-swica">
        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <div className="eyebrow mb-2" style={{ color: "var(--primary)" }}>Assurance complémentaire</div>
            <h3 className="font-display text-2xl md:text-3xl tracking-tight">
              Tes cours remboursés <span style={{ color: "var(--primary)" }}>jusqu'à 97 %</span> par ton assurance
            </h3>
            <p className="mt-3 text-[var(--text)]">
              Grâce à notre partenariat <strong>SWICA</strong>, tes cours de danse peuvent être largement remboursés par ton assurance complémentaire (Completa Forte, Praevita, Optima). Un accompagnement gratuit + un cadeau offert lors du rendez-vous.
            </p>
          </div>
          <Link to="/assurances" data-testid="tarifs-swica-cta" className="btn-primary shrink-0" style={{ padding: "14px 28px" }}>
            Découvrir l'offre SWICA <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="mt-20 card-flat text-center" style={{ background: "var(--primary)", color: "#fff", borderColor: "transparent", padding: "48px 32px" }}>
        <h3 className="font-display text-3xl md:text-4xl">Prêt·e à nous rejoindre&nbsp;?</h3>
        <p className="text-white/90 mt-3">Inscris-toi en ligne ou contacte-nous pour toute question.</p>
        <Link to="/inscription" data-testid="tarifs-cta" className="inline-flex items-center gap-2 mt-6 px-8 py-4 rounded-full bg-white font-semibold" style={{ color: "var(--primary)" }}>
          S'inscrire <ArrowRight size={16} />
        </Link>
      </section>
      </div>
    </div>
  );
}
